import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, Loader2 } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { WhatsAppConversation, whatsappInboxService } from '@/services/integrations/whatsappInboxService';
import { WhatsAppSocketProvider } from '@/hooks/useWhatsAppSocket';
import ConversationList from './_components/ConversationList';
import ChatPanel from './_components/ChatPanel';
import StartConversationModal from './_components/StartConversationModal';

/** Top-level 3-pane WhatsApp Inbox layout (FE-17). Supports a `?lead=<uuid>`
 * deep link from the Lead Detail page (FE-20) via GET ?lead_uuid=. */
const WhatsAppInboxPage: React.FC = () => {
  const { userData } = useUser();
  const currentUserName = userData?.data?.userName || 'Agent';
  const [searchParams] = useSearchParams();
  const deepLinkLeadUuid = searchParams.get('lead');

  const [active, setActive] = useState<WhatsAppConversation | null>(null);
  const [resolvingDeepLink, setResolvingDeepLink] = useState(!!deepLinkLeadUuid);
  const [showStartModal, setShowStartModal] = useState(false);

  useEffect(() => {
    if (!deepLinkLeadUuid) return;
    whatsappInboxService.listConversations({ lead_uuid: deepLinkLeadUuid, page_size: 1 })
      .then((res) => { if (res.data[0]) setActive(res.data[0]); })
      .finally(() => setResolvingDeepLink(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkLeadUuid]);

  return (
    <WhatsAppSocketProvider>
      <div className="flex h-screen min-h-0 bg-gray-50">
        <ConversationList
          activeUuid={active?.uuid || null}
          onSelect={setActive}
        />

        {resolvingDeepLink ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-brandColor" />
          </div>
        ) : active ? (
          <ChatPanel
            key={active.uuid}
            conversation={active}
            currentUserName={currentUserName}
            onConversationPatch={(patch) => setActive((prev) => (prev ? { ...prev, ...patch } : prev))}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <MessageCircle className="w-16 h-16 mb-3 text-gray-300" />
            <p className="text-sm">
              {deepLinkLeadUuid ? 'This lead has no WhatsApp conversation yet.' : 'Select a conversation to start messaging'}
            </p>
            {deepLinkLeadUuid && (
              <button
                onClick={() => setShowStartModal(true)}
                className="mt-3 px-4 py-2 rounded-lg bg-[#153d23] text-white text-[12.5px] font-bold hover:opacity-90"
              >
                Start conversation
              </button>
            )}
          </div>
        )}
      </div>

      {showStartModal && deepLinkLeadUuid && (
        <StartConversationModal
          lead={{ uuid: deepLinkLeadUuid }}
          onClose={() => setShowStartModal(false)}
          onResolved={setActive}
        />
      )}
    </WhatsAppSocketProvider>
  );
};

export default WhatsAppInboxPage;
